import { useGetAllVersionsCountQuery } from "@api/versions";
import { useCallback, useEffect, useMemo } from "react";
import { useAccountStoreShallow } from "@/features/account-management";
import { useAuthStoreShallow } from "@/features/authentication";
import {
  useGetPagesCountQuery,
  useGetUsersCountQuery,
} from "@/shared/services";
import { useAnalyticsStoreShallow } from "../store";
import { register } from "../utils";

export const useRegisterCounts = (authenticated = false) => {
  // Skip all API calls if not authenticated
  const skipQueries = !authenticated;

  const { data: allPagesCount, isLoading: allPagesCountLoading } =
    useGetPagesCountQuery({}, { skip: skipQueries });
  const { data: livePagesCount, isLoading: livePagesCountLoading } =
    useGetPagesCountQuery({ status: "Live" }, { skip: skipQueries });
  const { data: allVersionsCount, isLoading: allVersionsCountLoading } =
    useGetAllVersionsCountQuery({}, { skip: skipQueries });

  const { data: ownPagesCount, isLoading: ownPagesCountLoading } =
    useGetPagesCountQuery({ onlyOwnStories: true }, { skip: skipQueries });
  const { data: ownLivePagesCount, isLoading: ownLivePagesCountLoading } =
    useGetPagesCountQuery(
      { status: "Live", onlyOwnStories: true },
      { skip: skipQueries },
    );
  const { seats } = useAccountStoreShallow((s) => ({
    seats: s.seats,
  }));

  const { data: usersCount, isLoading: usersCountLoading } =
    useGetUsersCountQuery();
  const { userType } = useAuthStoreShallow((s) => ({
    userType: s.userType,
  }));

  const loading = useMemo(() => {
    const loadingStates = [
      allPagesCountLoading,
      livePagesCountLoading,
      allVersionsCountLoading,
      ownPagesCountLoading,
      ownLivePagesCountLoading,
      usersCountLoading,
    ];
    return loadingStates.some(Boolean);
  }, [
    allPagesCountLoading,
    livePagesCountLoading,
    allVersionsCountLoading,
    ownPagesCountLoading,
    ownLivePagesCountLoading,
    usersCountLoading,
  ]);

  const setInitializedType = useAnalyticsStoreShallow(
    (s) => s.setInitializedType,
  );

  // Memoize analytics properties to prevent unnecessary re-registrations
  const analyticsProperties = useMemo(() => {
    if (!authenticated || loading) return null;

    return {
      "organization stories": allPagesCount?.count ?? 0,
      "organization active stories": livePagesCount?.count ?? 0,
      "organization versions": allVersionsCount?.count ?? 0,
      "user active stories": ownLivePagesCount?.count ?? 0,
      "user stories": ownPagesCount?.count ?? 0,
      "users count": usersCount?.count ?? 0,
      "seats count": seats.planLimit,
      "available seats count": seats.additionalSeats,
      "user type": userType,
    };
  }, [
    authenticated,
    loading,
    seats,
    allPagesCount?.count,
    livePagesCount?.count,
    allVersionsCount?.count,
    ownLivePagesCount?.count,
    ownPagesCount?.count,
    usersCount?.count,
    userType,
  ]);

  const registerProperties = useCallback(() => {
    if (analyticsProperties) {
      register(analyticsProperties);
      setInitializedType("properties");
    }
  }, [analyticsProperties, setInitializedType]);

  useEffect(() => {
    registerProperties();
  }, [registerProperties]);
};
